import { forwardRef } from '@chakra-ui/react';
import { useMemo } from 'react';

import { Box, type BoxProps, Flex } from '@frontend/components';

const DEFAULT_DROP_AREA_HEIGHT = 3;

export type BetweenItemDropBorderProps = {
  side: 'top' | 'bottom';
  canDrop: boolean;
  isOver: boolean;
  dropAreaProps?: BoxProps;
  centerInContainer?: boolean;
};

export const BetweenItemDropBorder = forwardRef<
  BetweenItemDropBorderProps,
  'div'
>(
  (
    { side, canDrop, isOver, dropAreaProps = {}, centerInContainer = false },
    forwardedRef,
  ) => {
    const {
      h: dropAreaHProp,
      height: dropAreaHeightProp,
      ...restDropAreaProps
    } = dropAreaProps;

    const dropAreaHeight =
      dropAreaProps?.h ?? dropAreaProps?.height ?? DEFAULT_DROP_AREA_HEIGHT;

    const isBottom = side === 'bottom';

    const positioning = useMemo(() => {
      // When centerInContainer is true, position at 50% regardless of side
      if (centerInContainer) {
        return {
          top: '50%',
          bottom: undefined,
          transform: 'translateY(-50%)',
        };
      }
      return {
        top: isBottom ? undefined : 0,
        bottom: isBottom ? 0 : undefined,
        transform: `translateY(${isBottom ? '50%' : '-50%'})`,
      };
    }, [centerInContainer, isBottom]);

    return (
      <Flex
        zIndex="overlay"
        position="absolute"
        {...positioning}
        flexDirection="column"
        justifyContent="center"
        width="100%"
        height={0}
      >
        <Box
          zIndex={1}
          position="absolute"
          transform={`scaleY(${canDrop ? 1 : 0})`}
          width="100%"
          transitionProperty="common"
          transitionDuration="faster"
          {...restDropAreaProps}
          height={dropAreaHeight}
          ref={forwardedRef}
        />
        <Box
          zIndex={0}
          position="absolute"
          transform={`scaleY(${isOver ? 1 : 0})`}
          width="100%"
          height={0.5}
          backgroundColor="interaction.hover"
          transitionProperty="common"
          transitionDuration="faster"
        />
      </Flex>
    );
  },
);
